/**
 * AppHomePage Component
 * 
 * Main dashboard/homepage for authenticated users.
 * Features NextMoveCard, My Clubs, and horizontal event scroll.
 */

import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import TopBar from '../components/TopBar';
import NextMoveCard from '../components/NextMoveCard';
import EventCard from '../components/EventCard';
import ClubCard from '../components/ClubCard';
import { useAllEvents } from '../hooks/useEvents';
import { useClubs } from '../hooks/useClubs';
import { useNowSeconds } from '../hooks/useNowSeconds';
import { Button, EventCardSkeleton, ClubCardSkeleton, NextMoveCardSkeleton, PullToRefresh } from '../components';
import { EmptyEventsFallback } from '../components/EmptyEventsFallback';
import { POST_EVENT_VISIBILITY_BUFFER_SECONDS } from '../lib/datetime';

export default function AppHomePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // #333: Pull-to-refresh refetches the two queries this page renders from —
  // the events feed (['events', ...]) and the user's clubs (['clubs']).
  // Awaiting refetchQueries keeps the spinner up until fresh data lands.
  const handleRefresh = async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['events'] }),
      queryClient.refetchQueries({ queryKey: ['clubs'] }),
    ]);
  };

  // #1693/#1899: live nowSeconds via useNowSeconds keeps the upcoming/past
  // split fresh on a 60s tick without a refetch when the tab is left open
  // overnight (the original #770 use case). Sibling-site migration of the
  // bespoke setTick pattern PR #1887 missed — see #1899. Inline derivation
  // (not isPastEvent(), which reads Date.now()) keeps the impure read out
  // of render — purity rule from #1689.
  const nowSeconds = useNowSeconds();
  const isPastAtNow = (event: { starts_at: number; ends_at?: number | null }) => {
    const effectiveEnd = event.ends_at ?? event.starts_at;
    return effectiveEnd + POST_EVENT_VISIBILITY_BUFFER_SECONDS < nowSeconds;
  };

  // Fetch user's events and clubs
  const { data: events = [], isLoading: eventsLoading } = useAllEvents();
  const { data: clubs = [], isLoading: clubsLoading } = useClubs();

  // #1782: !isPastAtNow keeps an event in upcoming for 1h after its effective
  // end (ends_at when present, else starts_at) — covers events still happening.
  const upcomingEvents = events
    .filter(event => !isPastAtNow(event))
    .sort((a, b) => a.starts_at - b.starts_at) // Chronological order (earliest first)
    .slice(0, 15); // Show first 15 events for better discovery

  // Get next RSVP'd event for NextMoveCard (only show events user has RSVP'd to)
  const rsvpdEvents = events
    .filter(event =>
      !isPastAtNow(event) &&
      (event.rsvp_status === 'yes' || event.rsvp_status === 'maybe')
    )
    .sort((a, b) => a.starts_at - b.starts_at);
  
  const nextEvent = rsvpdEvents[0];

  // #3059: role/state-aware Home empty sibling — same diagnosis as the events
  // tab. Create Event only for leaders/owners; otherwise point at club discovery.
  const isLeaderOfAnyClub = clubs.some(
    (c) => c.role === 'owner' || c.role === 'leader',
  );
  const hasPastEvents = events.some(isPastAtNow);

  return (
    <>
      {/* Back button added for consistency with other pages (#1095) */}
      <TopBar title="Home" onBack={() => navigate(-1)} />
      {/* topOffset=56 pushes the PTR spinner below the fixed TopBar (h-14) — #1992 */}
      <PullToRefresh onRefresh={handleRefresh} topOffset={56}>
        <div className="pt-14">
        <div className="px-4 py-6 space-y-8">
          {/* Hero - Next Move */}
          {eventsLoading ? (
            <NextMoveCardSkeleton />
          ) : nextEvent ? (
            <section>
              <NextMoveCard
                title={nextEvent.title}
                subtitle={`${nextEvent.club_name || 'Event'} • ${new Date(nextEvent.starts_at * 1000).toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}`}
                event={{
                  ...nextEvent,
                  startTime: new Date(nextEvent.starts_at * 1000),
                  userRsvpStatus: nextEvent.rsvp_status,
                  rsvpCount: nextEvent.rsvp_count || 0,
                  distanceM: nextEvent.distance_m, // Issue #1083: meters canonical unit
                  // registrationStatus omitted: backend doesn't expose it yet (#1797)
                }}
                primaryAction={{
                  label: 'View Event',
                  onClick: () => navigate(`/event/${nextEvent.short_code || nextEvent.id}`)
                }}
                secondaryAction={{
                  label: 'View Club',
                  onClick: () => navigate(`/clubs/${nextEvent.club_slug}`)
                }}
                backgroundImage={nextEvent.hero_image_url || undefined} // Backend COALESCE provides fallback
                className="mb-2"
              />
            </section>
          ) : (
            <section>
              <NextMoveCard
                title={upcomingEvents.length > 0 ? "Your Next Event" : clubs.length === 0 ? "Welcome!" : "Welcome back!"}
                subtitle={upcomingEvents.length > 0 
                  ? "RSVP to events to see your next move here"
                  : "No upcoming events yet. Explore clubs to find activities."
                }
                primaryAction={{
                  label: upcomingEvents.length > 0 ? 'Browse Events' : 'Browse Clubs',
                  onClick: () => navigate(upcomingEvents.length > 0 ? '/events' : '/clubs')
                }}
                secondaryAction={{
                  label: upcomingEvents.length > 0 ? 'View Clubs' : 'Create Event', 
                  onClick: () => navigate(upcomingEvents.length > 0 ? '/clubs' : '/events/create')
                }}
                className="mb-2"
              />
            </section>
          )}

          {/* My Clubs - Browse All link for club discovery (#1339) */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-text-primary">My Clubs</h2>
              <button
                className="text-accent hover:text-accent-light transition-colors text-xs"
                onClick={() => navigate('/clubs')}
              >
                Browse All
              </button>
            </div>
            
            {clubsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <ClubCardSkeleton key={i} />
                ))}
              </div>
            ) : clubs.length > 0 ? (
              <div className="space-y-3">
                {[...clubs]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((club) => (
                    <ClubCard
                      key={club.id}
                      club={club}
                      onClick={() => navigate(`/clubs/${club.slug}`)}
                    />
                  ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-text-muted mb-4">You haven't joined any clubs yet</p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/clubs/create')}
                >
                  Create Club
                </Button>
              </div>
            )}
          </section>

          {/* Upcoming Events - Club Events (Expanded) */}
          <section className="pb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-text-primary">Upcoming Events</h2>
              <button 
                className="text-accent hover:text-accent-light transition-colors text-xs"
                onClick={() => navigate('/events')}
              >
                View All
              </button>
            </div>
            
            {eventsLoading ? (
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                {[1, 2, 3, 4].map((i) => (
                  <EventCardSkeleton key={i} />
                ))}
              </div>
            ) : upcomingEvents.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                {upcomingEvents.map((event) => (
                  <EventCard
                    key={event.id}
                    event={{
                      id: event.id,
                      short_code: event.short_code,
                      title: event.title,
                      date: new Date(event.starts_at * 1000).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      }),
                      starts_at: event.starts_at,
                      ends_at: event.ends_at || undefined,
                      location: event.location_name || event.location_address || 'TBD',
                      attendees: event.rsvp_count || 0,
                      imageUrl: event.card_image_url || event.hero_image_url || '', // #1962 prefers card-sized variant; COALESCE upstream handles event → club fallback
                      distance_m: event.distance_m || undefined, // Issue #1083: meters canonical unit
                      rsvp_count: event.rsvp_count || 0,
                      rsvp_status: event.rsvp_status || null,
                      training_group_name: event.training_group_name ?? null,
                      event_number: event.event_number ?? null
                    }}
                    onClick={() => navigate(`/event/${event.short_code || event.id}`)}
                  />
                ))}
              </div>
            ) : (
              <EmptyEventsFallback
                isLeaderOfAnyClub={isLeaderOfAnyClub}
                hasClubs={clubs.length > 0}
                hasPastEvents={hasPastEvents}
              />
            )}
          </section>
        </div>
        </div>
      </PullToRefresh>
    </>
  );
}